import { ArrayUtils, engineComponent, NumberUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Symbols } from "../GameComponents/Symbols";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { BaseView } from "../GameComponents/Base/BaseView";
import { Paylines } from "../GameComponents/Paylines";
import { FreespinsView } from "../GameComponents/Freespins/FreespinsView";
import { Communication } from "neo-games";
import { Symbol } from "../GameComponents/Symbol";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { BetManager } from "../Managers/BetManager";
import { AppContext } from "../../Common/AppContext";
import { Tweens } from "../Components/Effects/Tweens";
import { Payline } from "../GameComponents/Payline";
import { Configuration } from "../Components/Configuration";
import { Settings } from "./Settings";
import { SymbolExtended } from "./SymbolExtended";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { Languages } from "../Utils/Languages";
import { ProtonElement } from "../Components/Elements/Graphics/ProtonElement";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    private _paylines: Paylines;
    private _cycleAnimationName: string = "Value";
    private cycleCounter: ImageTextCounterElement = null;

    private _activeElementsNames: Array<string> = [];
    private _activeElements: Array<SwitchElement> = [];
    private _activeElementsCase: string;

    protected _symbolCaseReelDelay: number = 0;
    protected _counterStepFactor: number = 1.1;
    protected _counterStepDuration: number = 55;
    protected _counterKeyTimes: number[] = [0, 0.25, 0.75, 1];
    protected _counterSizes: number[] = [0, 3, 3, 2];
    protected _counterEase: string = "backInOut";
    protected _counterStepDelay: number = 0;
    protected _counterStepDelayWild: number = 0;

    protected _wildImpactLength: number = 1;

    protected static readonly CaseNormal = "NORMAL";
    protected static readonly CaseWin = "WIN";
    protected static readonly CaseNoWin = "NOWIN";
    protected static readonly CaseHighWin = "HIGHWIN";
    protected static readonly CaseCycle = "CYCLE";
    protected static readonly CaseCycleHigh = "CYCLE_HIGH";

    protected counterFX: SwitchElement;
    protected counter: ImageTextCounterElement;
    protected currentCounterStep: number;

    private _paylineWinDelay: number = 60; /* Delay between win lines */
    private _paylineWinEndDelay: number = 90; /* Delay after win lines, before cycle */
    private _paylineCycleDelay: number = 120; /* Delay between cycle lines */

    protected static readonly ShowFirstWinsStep = "showFirstWinsStep";
    protected static readonly ShowFirstWins = "showFirstWins";
    protected static readonly EndFirstWinsStep = "endFirstWinsStep";
    protected static readonly FinishWin = "finishWin";

    configure(node: Element): void {
        super.configure(node);

        if (node.hasAttribute(WinAnimatorExtendedAttributes.CYCLE_ANIMATION)) {
            this._cycleAnimationName = node.getAttribute(WinAnimatorExtendedAttributes.CYCLE_ANIMATION);
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS)) {
            this._activeElementsNames = node.getAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS).split(";");
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_DELAY)) {
            this._paylineWinDelay = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_DELAY));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_DELAY)) {
            this._paylineWinEndDelay = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_END_DELAY));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.PAYLINE_CYCLE_DELAY)) {
            this._paylineCycleDelay = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.PAYLINE_CYCLE_DELAY));
        }

        if (node.hasAttribute("SymbolCaseReelDelay")) {
            this._symbolCaseReelDelay = parseInt(node.getAttribute("SymbolCaseReelDelay"));
        }

        if (node.hasAttribute("CounterStepFactor")) {
            this._counterStepFactor = parseFloat(node.getAttribute("CounterStepFactor"));
        }
        if (node.hasAttribute("CounterStepDelay")) {
            this._counterStepDelay = parseInt(node.getAttribute("CounterStepDelay"));
        }
        if (node.hasAttribute("CounterStepDelayWild")) {
            this._counterStepDelayWild = parseInt(node.getAttribute("CounterStepDelayWild"));
        }
        if (node.hasAttribute("CounterStepDuration")) {
            this._counterStepDuration = parseInt(node.getAttribute("CounterStepDuration"));
        }
        if (node.hasAttribute("CounterKeyTimes")) {
            this._counterKeyTimes = node
                .getAttribute("CounterKeyTimes")
                .split(";")
                .map((v) => parseFloat(v));
        }
        if (node.hasAttribute("CounterSizes")) {
            this._counterSizes = node
                .getAttribute("CounterSizes")
                .split(";")
                .map((v) => parseInt(v));
        }
        if (node.hasAttribute("CounterEase")) {
            this._counterEase = node.getAttribute("CounterEase");
        }

        if (node.hasAttribute("WildImpactLength")) {
            this._wildImpactLength = parseInt(node.getAttribute("WildImpactLength"));
        }
    }

    initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        parent?: BaseView | FreespinsView,
    ): void {
        this._paylines = paylines;

        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        if (!this.value && this._cycleAnimationName && this.childs[this._cycleAnimationName]) {
            this.value = this.childs[this._cycleAnimationName];
            let childs = this.value.getElements().childs;
            for (let cName in childs) {
                if (childs[cName] instanceof ImageTextCounterElement) {
                    this.cycleCounter = childs[cName];
                }
            }
            this.animationsContainer.addChild(this.value);
        }

        if (parent) {
            this._activeElementsNames.forEach((elementName) => {
                if (parent.childs[elementName] && parent.childs[elementName] instanceof SwitchElement) {
                    this._activeElements.push(parent.childs[elementName]);
                }
            });
        }

        this.createCounter();
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    /**
     * Sorts symbols according to their zIndex attribute, if applicable
     */
    private sortSymbols(): void {
        if (!this.symbols.hasZindex) {
            return;
        }
        let addQueue: Symbol[] = [];
        this.symbolsContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.addChild(symbol);
        });
    }

    showSymbols(reelsData: Array<Communication.Game.IReel>) {
        super.showSymbols(reelsData);
        this.sortSymbols();
    }

    onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number) {
        super.onReelStopped(reelsData, reelIndex);
        if (this.currentSymbols[reelIndex]) {
            this.currentSymbols[reelIndex].forEach((symbol) => {
                if (symbol.hasCase("IMPACT")) {
                    symbol.goToCase("IMPACT");
                }
            });
        }
        this.sortSymbols();
    }

    //******************************************************************************************************************
    //  WINS
    //******************************************************************************************************************

    showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        let ret = super.showWins(wins, win);

        PlayList.stop("SND_BaseRotationStart");

        if (win > 0) {
            this._paylines.visible = true;

            if (this.currentMultiplierIndex > 1) {
                // priority: HIGH + number -> HIGH -> WIN
                this.setActiveElementCaseArray([
                    WinAnimatorExtended.CaseHighWin + this.currentMultiplierIndex,
                    WinAnimatorExtended.CaseHighWin,
                    WinAnimatorExtended.CaseWin,
                ]);
            } else {
                // priority: WIN
                this.setActiveElementCaseArray([WinAnimatorExtended.CaseWin]);
            }
        } else {
            // priority: NOWIN
            this.setActiveElementCaseArray([WinAnimatorExtended.CaseNoWin]);
        }

        return ret;
    }

    protected showCustomWin(): boolean {
        return super.showCustomWin();
    }

    protected winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean): Array<Symbol> {
        const win = this.currentWins[winIndex];
        switch (caseType) {
            case "WIN":
                if (this.currentMultiplierIndex > 0) {
                    break;
                }
            case "CYCLE":
                switch (win.symbolMultiplier || 1) {
                    case 1:
                        this.winSymbolsGoToCase(winIndex, "X1", useValue);
                        break;
                    case 2:
                        this.winSymbolsGoToCase(winIndex, "X2", useValue);
                        break;
                    case 4:
                        this.winSymbolsGoToCase(winIndex, "X4", useValue);
                        break;
                    case 8:
                        this.winSymbolsGoToCase(winIndex, "X8", useValue);
                        break;
                }
                break;
        }

        let ret = super.winSymbolsGoToCase(winIndex, caseType, useValue);

        if (useValue && ret.length > 0 && win.amount > 0) {
            let off = 0;
            if (this.cycleCounter) {
                off = Math.floor((this.cycleCounter.scale.x * this.cycleCounter.bitmapText.textWidth) / 2);
            }

            this.value.position.x = ret[0].position.x + ret[0].offsetX + off;
        }

        return ret;
    }

    protected winSymbolsGoToCaseAsWay(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];
        let lineSymbolId = win.symbols.filter((id) => id != Settings.SymbolWild)[0] || Settings.SymbolWild;

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];

                    if ((caseType == "WIN" && this.currentMultiplierIndex == 0) || caseType == "CYCLE") {
                        if (symbol.id === Settings.SymbolWild - 1 && lineSymbolId !== Settings.SymbolWild) {
                            symbol.goToCase("TO_" + caseType, false);
                            symbol.goToCase("TO_" + lineSymbolId, false, i * this._symbolCaseReelDelay);
                        } else {
                            symbol.goToCase(caseType, false, i * this._symbolCaseReelDelay);
                        }
                    } else {
                        symbol.goToCase(caseType, false);
                    }

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    protected getCycleWinsDelay(): number {
        return this._paylineCycleDelay;
    }

    //******************************************************************************************************************
    //  COUNTER
    //******************************************************************************************************************

    protected createCounter(): void {
        this.counterFX = this.childs["CounterFX"];
        this.counter = this.childs["Counter"];
        this.animationsContainer.addChild(this.counterFX);
        this.animationsContainer.addChild(this.counter);
    }

    protected resetCounter(): void {
        Tweens.removeTweens(this.counter, "all");

        this.counter.alpha = 0;
        this.counterFX.alpha = 0;
    }

    private getCounterStep(): number {
        return (0.8 - 0.5) / this.currentWins.length;
    }

    protected runCounterStep(): void {
        PlayList.play("SND_BaseWin" + (this.currentCounterStep < 7 ? this.currentCounterStep + 1 : 8));
        PlayList.play("SND_BasePayline" + Math.min(this.currentCounterStep + 1, 16));

        this.counter.visible = true;
        this.counterFX.visible = true;
        this.counterFX.scale.x = 1;
        this.counterFX.scale.y = 1;

        let isLowWin = this.currentWin <= BetManager.Bet;
        let scaleTweenDuration = isLowWin ? 0 : this._counterStepDuration;

        let particles = <ProtonElement>this.counterFX.getChildElementByName("CounterParticles");
        if (particles != null) {
            particles["alpha"] = isLowWin ? 0 : 1;
        }

        let amountFrom = "0";
        let amountTo = "0";

        for (let i = 0; i <= this.currentCounterStep; i++) {
            const win = this.currentWins[i];

            if (win.type === Communication.Game.WinType.Cash) {
                if (i < this.currentCounterStep) {
                    amountFrom = NumberUtils.add(amountFrom, this.currentWins[i].amount);
                }

                amountTo = NumberUtils.add(amountTo, this.currentWins[i].amount);
            }
        }
        let delay: number = 0;
        const win = this.currentWins[this.currentCounterStep];
        /*
        if(this.currentCounterStep > 0 && win && win.symbols) {
            //delay = win.symbols.filter(i => i > 0).length * this._symbolCaseReelDelay;
            if(win.symbols.indexOf(Settings.SymbolWild) >= 0) {
                delay += this._counterStepDelayWild;
            } else {
                delay += this._counterStepDelay;
            }
        }
        */

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            this.counter.start(
                parseInt(NumberUtils.devide(amountFrom, BetManager.CoinSize).toString()),
                parseInt(NumberUtils.devide(amountTo, BetManager.CoinSize).toString()),
                0,
                delay,
            );
        } else {
            ImageTextCounterElement.Round = false;
            this.counter.start(parseFloat(amountFrom), parseFloat(amountTo), 0, delay);
        }

        const step = this.getCounterStep();

        if (this.currentCounterStep === 0) {
            this.counter.alpha = 0;
            this.counterFX.goToCase("FLARE");
        } else {
            this.counterFX.goToCase("FLARE", false, delay);
        }

        Tweens.removeTweens(this.counter, "all");
        Tweens.alphaTween(this.counter, {
            alphas: [this.counter.alpha, 1],
            tweenDuration: 10,
            tweenDelay: delay,
        });

        let values: Array<PIXI.Point> = [];

        this._counterSizes.forEach((value) => {
            if (value <= 0) {
                values.push(new PIXI.Point(this.counter.scale.x, this.counter.scale.y));
            } else {
                values.push(
                    new PIXI.Point(
                        0.5 + step * (this.currentCounterStep + value - 1),
                        0.5 + step * (this.currentCounterStep + value - 1),
                    ),
                );
            }
        });

        Tweens.scaleTween(this.counter, {
            tweenValues: values,
            tweenDuration: scaleTweenDuration,
            keyTimes: this._counterKeyTimes,
            tweenDelay: delay,
            tweenEase: this._counterEase,
        });
    }

    protected runCounterOutro(reset: boolean = false): void {
        this.setActiveElementCaseArray(["COUNTER_OUTRO"]);

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            this.counter.start(0, parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()), 0);
        } else {
            ImageTextCounterElement.Round = false;
            this.counter.start(0, this.currentWin, 0);
        }

        this.counterFX.scale.x = 1;
        this.counterFX.scale.y = 1;

        let isLowWin = this.currentWin <= BetManager.Bet;
        let counterSize = 0.5 + this.getCounterStep() * (reset ? this.currentWins.length : this.currentCounterStep);

        let finish = (reset: boolean = false) => {
            /*this.counterFX.goToOutro();*/
            this.counterFX.goToCase("OUTRO_END");
            Tweens.removeTweens(this.counter);
            Tweens.alphaTween(this.counter, {
                alphas: [1, 0],
                tweenDuration: 30,
                tweenDelay: 5,
                callbackFunction: (params) => {
                    if (reset) {
                        PlayList.stop("SND_BaseWinFinal");
                    }
                    PlayList.play("SND_BaseWinAway");
                },
                callbackType: "onStart",
            });
            Tweens.scaleTween(this.counter, {
                tweenValues: [
                    new PIXI.Point(counterSize, counterSize),
                    new PIXI.Point(counterSize + 1, counterSize + 1),
                ],
                tweenDuration: 35,
                tweenDelay: 0,
                tweenEase: "sineIn",
            });
        };

        Tweens.removeTweens(this.counter, "all");
        if (reset) {
            finish(true);
            this.counterFX.goToCase("OUTRO_END");
        } else {
            this.counterFX.goToCase("OUTRO_START");
            Tweens.scaleTween(this.counterFX, {
                tweenValues: [
                    new PIXI.Point(counterSize, counterSize),
                    new PIXI.Point(counterSize + 0.3, counterSize + 0.3),
                    new PIXI.Point(counterSize, counterSize),
                ],
                tweenDuration: 15,
            });
            Tweens.scaleTween(this.counter, {
                tweenValues: [
                    new PIXI.Point(counterSize, counterSize),
                    new PIXI.Point(counterSize + 0.3, counterSize + 0.3),
                    new PIXI.Point(counterSize, counterSize),
                    new PIXI.Point(counterSize, counterSize),
                ],
                keyTimes: [0, 0.125, 0.25, 1],
                tweenDuration: 60,
                callbackFunction: finish,
            });
        }
        PlayList.play("SND_BaseWinEnd");
    }

    protected showFirstWins(): void {
        let delay: number = 0;

        this.currentSymbols.forEach((reel, reelIndex) => {
            reel.forEach((symbol) => {
                if (symbol && symbol.id === Settings.SymbolWild - 1) {
                    delay = this._wildImpactLength - (this.reelCount - 1 - reelIndex) * this._symbolCaseReelDelay;
                }
            });
        });
        if (delay < 0) {
            delay = 0;
        }
        delay += 1;

        if (this.currentMultiplierIndex === 0) {
            this.currentCounterStep = 0;
            this.startTimer(WinAnimatorExtended.ShowFirstWinsStep, delay);
        } else {
            // play base win sounds up to the number of wins
            for (let i = 0; i < 8; i++) {
                PlayList.play("SND_BaseWin" + (i + 1));
                if (i === 8) {
                    break;
                }
            }
            // play a chord
            /*
            let j = 0;
            for(let i = 0; i < 9; i++) {
                if(j <= this.currentWins.length && (i === 0 || i === 2 || i === 4 || i === 7)) {
                    PlayList.play('SND_BaseWin' + (i+1) );
                    j++;
                }
            }
             */
            // play the highest sound
            //PlayList.play('SND_BaseWin' + (this.currentWins.length < 10 ? this.currentWins.length : 10 ));
            this.startTimer(WinAnimatorExtended.ShowFirstWins, delay);
        }
    }

    protected showFirstWinsStep(): void {
        const win = this.currentWins[this.currentCounterStep];

        if (this.currentCounterStep == 0) {
            this.allPaylinesGoToCase(Payline.CaseNowin);
            this.allSymbolsGoToCase(Symbol.CaseNowin, true);

            for (let i = 0; i < this.currentWins.length; i++) {
                this.winPaylineGoToCase(i, Payline.CaseNormal);
                this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
            }
        }

        this.winPaylineGoToCase(this.currentCounterStep, Payline.CaseWin);
        const symbols = this.winSymbolsGoToCase(this.currentCounterStep, Symbol.CaseWin, false);

        this.runCounterStep();

        this.currentCounterStep++;

        if (this.currentWin > BetManager.Bet) {
            if (this.currentCounterStep < this.currentWins.length) {
                this.startTimer(
                    WinAnimatorExtended.ShowFirstWinsStep,
                    this._paylineWinDelay + (symbols.length - 1) * this._symbolCaseReelDelay,
                );
            } else {
                this.startTimer(
                    WinAnimatorExtended.FinishWin,
                    this._paylineWinDelay / 2 + (symbols.length - 1) * this._symbolCaseReelDelay,
                );
            }
        } else {
            this.startTimer(WinAnimatorExtended.FinishWin, 10);
        }
    }

    protected finishWin(): void {
        this.emit(WinAnimator.FinishWinCalculation);
        this.runCounterOutro();
        this.startTimer(WinAnimatorExtended.EndFirstWinsStep, this._paylineWinEndDelay);
    }

    onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowFirstWinsStep:
                this.showFirstWinsStep();
                break;
            case WinAnimatorExtended.ShowFirstWins:
                super.showFirstWins();
                break;
            case WinAnimatorExtended.EndFirstWinsStep:
                this.emit(WinAnimator.FinishWinAnimator);
                this.currentCycle = 0;
                this.cycleWins(true);
                break;
            case WinAnimatorExtended.FinishWin:
                this.finishWin();
                break;
            default:
                super.onTimerComplete();
                break;
        }
    }

    protected cycleWins(isFirst: boolean = false) {
        /*
        // Do not cycle single win
        if(!this.currentWins || this.currentWins.length <= 1 && !isFirst) {
            this.idle();
            return;
        }
        */

        if (isFirst) {
            if (this.currentMultiplierIndex > 1) {
                // priority: HIGH + number -> HIGH -> WIN
                this.setActiveElementCaseArray([
                    WinAnimatorExtended.CaseCycleHigh + this.currentMultiplierIndex,
                    WinAnimatorExtended.CaseCycleHigh,
                    WinAnimatorExtended.CaseCycle,
                ]);
            } else {
                // priority: WIN
                this.setActiveElementCaseArray([WinAnimatorExtended.CaseCycle]);
            }
        }

        PlayList.stop("SND_BaseRecapLoop3");
        PlayList.stop("SND_BaseRecapLoop4");
        if (this.currentWins) {
            PlayList.play("SND_BaseRecapLoop" + this.currentWins[this.currentCycle].symbols.length);
        }

        super.cycleWins(isFirst);
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin = false): boolean {
        this._paylines.visible = false;

        this.setActiveElementCaseArray(["RESET"]);
        if (spin) {
            this.setActiveElementCaseArray(["SPIN"]);
            PlayList.stop("SND_BaseRecapLoop3");
            PlayList.stop("SND_BaseRecapLoop4");
        }

        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowFirstWinsStep:
                this.runCounterOutro(true);
                break;
            case WinAnimatorExtended.ShowFirstWins:
                break;
            case WinAnimatorExtended.FinishWin:
                this.runCounterOutro(true);
                this.currentCounterStep = null;
                this.timerStatus = null;
                this.currentCycle = 0;
                break;
            case WinAnimatorExtended.EndFirstWinsStep:
                this.currentCounterStep = null;
                this.timerStatus = null;
                this.currentCycle = 0;
                break;
        }

        return super.reset(showLastSymbols, spin);
    }

    //******************************************************************************************************************
    //  ACTIVE ELEMENTS
    //******************************************************************************************************************

    /**
     * Attempts to switch all Active Elements to the earliest case from array the element can be switched to.
     * Elements with no case from the list are ignored.
     * @param s Array of names of cases to try to switch to
     * @param emitEvent
     * @param delay Switch delay
     */
    private setActiveElementCaseArray(s: Array<string>, emitEvent: boolean = false, delay?: number) {
        if (!s.length) {
            return;
        }
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s[0];
        this._activeElements.forEach((element) => {
            for (let i = 0; i < s.length; i++) {
                if (element.hasCase(s[i])) {
                    element.goToCase(s[i], emitEvent, delay);
                    break;
                }
            }
        });
    }

    /**
     * Attempts to switch all Active Elements to the given case if they have this case.
     * Elements without the case are ignored.
     * @param s Case name
     * @param emitEvent
     * @param delay
     */
    private setActiveElementCase(s: string, emitEvent: boolean = false, delay?: number) {
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s;
        this._activeElements.forEach((element) => {
            if (element.hasCase(s)) {
                element.goToCase(s, emitEvent, delay);
            }
        });
    }

    /**
     * Returns last case switched to, or the first case from a list switched to.
     */
    private getActiveElementCase(): string {
        return this._activeElementsCase;
    }
}

export const WinAnimatorExtendedAttributes = {
    ACTIVE_ELEMENTS: "ActiveElements",
    CYCLE_ANIMATION: "CycleAnimation",
    PAYLINE_WIN_DELAY: "PaylineWinDelay",
    PAYLINE_WIN_END_DELAY: "PaylineWinEndDelay",
    PAYLINE_CYCLE_DELAY: "PaylineCycleDelay",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/